import { prefersReducedMotion, RUNTIME_ROOT_SELECTOR } from './dom.js';

const RAIL_SELECTOR = '[data-bp-rail]';
const NAV_SELECTOR = '[data-bp-rail-nav]';
const WRAPPER_SELECTOR = '[data-bp-rail-wrap]';
const READY_ATTRIBUTE = 'data-bp-ready';
const NAME_ATTRIBUTE = 'data-bp-rail-name';
const LABEL_ATTRIBUTE = 'data-bp-rail-label';
const END_TOLERANCE = 1;
const FOCUSABLE_SELECTOR = 'a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])';

function scrollBehavior() {
  return prefersReducedMotion(globalThis) ? 'auto' : 'smooth';
}

function renderedElement(element) {
  let rendered = element;
  while (rendered && getComputedStyle(rendered).display === 'contents') {
    rendered = rendered.firstElementChild;
  }
  return rendered;
}

function firstRenderedChild(element) {
  return renderedElement(element.firstElementChild);
}

function renderedChildren(element) {
  const children = [];
  const append = (parent) => {
    for (const child of parent.children) {
      if (getComputedStyle(child).display === 'contents') append(child);
      else children.push(child);
    }
  };
  append(element);
  return children;
}

export function measureRailStep(rail) {
  const card = firstRenderedChild(rail);

  if (!card) return rail.clientWidth;

  const railStyle = getComputedStyle(rail);
  const gap = Number.parseFloat(railStyle.columnGap || railStyle.gap) || 0;
  return card.getBoundingClientRect().width + gap;
}

function centerAuthoredStart(rail) {
  const rawIndex = rail.getAttribute('data-bp-rail-start');
  if (rawIndex === null || rail.scrollWidth <= rail.clientWidth + 1) return;

  const index = Number.parseInt(rawIndex, 10);
  if (!Number.isInteger(index) || index < 0) return;

  const cards = renderedChildren(rail);
  const card = cards[index];
  if (!card) return;

  const railRect = rail.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const cardWidth = card.offsetWidth || cardRect.width;
  const cardLeft = cardRect.left - railRect.left + rail.scrollLeft;
  rail.scrollLeft = cardLeft - (rail.clientWidth - cardWidth) / 2;
}

function controlsRail(button, rail, railCount) {
  const target = button.getAttribute('data-bp-rail-for');
  if (target) return target === rail.getAttribute('data-bp-rail');

  const wrapper = button.closest(WRAPPER_SELECTOR);
  if (wrapper) return rail.closest(WRAPPER_SELECTOR) === wrapper;
  return railCount === 1;
}

function belongsToRoot(element, root) {
  return element.closest(RUNTIME_ROOT_SELECTOR) === root;
}

function findRails(root) {
  const rails = Array.from(root.querySelectorAll(RAIL_SELECTOR))
    .filter((rail) => belongsToRoot(rail, root));
  if (root.matches?.(RAIL_SELECTOR)) rails.unshift(root);
  return rails;
}

// A rail with nothing focusable rendered inside is unreachable by keyboard: make the scroller itself a named stop.
// shortcut: judged once at start-up, so a later resize that hides every card link keeps no stop; re-check on resize if a deck needs it.
export function makeRailFocusable(rail) {
  if (rail.hasAttribute('tabindex')) return false;
  if (Array.from(rail.querySelectorAll(FOCUSABLE_SELECTOR)).some((el) => el.getClientRects().length)) return false;
  rail.setAttribute('tabindex', '0');
  if (!rail.hasAttribute('role')) rail.setAttribute('role', 'region');
  if (!rail.hasAttribute('aria-label') && !rail.hasAttribute('aria-labelledby')) {
    const heading = rail.closest('section')?.querySelector('h1,h2,h3,h4,h5,h6');
    const name = heading?.textContent.replace(/\s+/gu, ' ').trim();
    if (name) rail.setAttribute('aria-label', name);
  }
  return true;
}

function usesNativeEnter(control) {
  return control.tagName === 'BUTTON'
    || (control.tagName === 'A' && control.getAttribute('href') !== null);
}

export function initializeRails(root) {
  if (root.getAttribute(READY_ATTRIBUTE) === 'rail') return 0;

  const rails = findRails(root);
  if (!rails.length) return 0;
  const authoredName = root.getAttribute(NAME_ATTRIBUTE);
  const authoredLabel = root.getAttribute(LABEL_ATTRIBUTE);
  if (rails.length === 1) {
    if (authoredName) rails[0].setAttribute('data-bp-rail', authoredName);
    if (authoredLabel) rails[0].setAttribute('aria-label', authoredLabel);
  }

  const buttons = Array.from(root.querySelectorAll(NAV_SELECTOR))
    .filter((button) => belongsToRoot(button, root));
  for (const rail of rails) {
    centerAuthoredStart(rail);
    makeRailFocusable(rail);

    for (const button of buttons) {
      if (!controlsRail(button, rail, rails.length)) continue;

      const scroll = (event) => {
        event.preventDefault();
        // Webflow delegates in-page anchor scrolling on the document, so the arrow href moves the page unless the click stops here.
        event.stopPropagation();
        const direction = button.getAttribute('data-bp-rail-nav') === 'next' ? 1 : -1;
        const behavior = scrollBehavior();
        const end = rail.scrollWidth - rail.clientWidth;

        if (direction === 1 && end > 0 && rail.scrollLeft >= end - END_TOLERANCE) {
          rail.scrollTo({ left: 0, behavior });
          return;
        }

        rail.scrollBy({ left: direction * measureRailStep(rail), behavior });
      };

      button.addEventListener('click', scroll);
      button.addEventListener('keydown', (event) => {
        if (event.key === ' ' || (event.key === 'Enter' && !usesNativeEnter(button))) scroll(event);
      });
    }
  }

  root.setAttribute(READY_ATTRIBUTE, 'rail');
  return rails.length;
}
